import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReceiptModelsTab from './ReceiptModelsTab';
import { requestNotificationPermission, showNotification } from '../utils/notifications';
import './ConfigModal.css';

const ConfigModal = ({ isOpen, onClose }) => {
      const [config, setConfig] = useState({
      whatsappApiUrl: 'https://graph.facebook.com/v18.0',
      phoneNumberId: '',
      accessToken: '',
      accessTokenExists: false,
      verifyToken: 'mi_token_secreto',
      wabaId: ''
      });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('whatsapp'); // 'whatsapp', 'mikrowisp', 'tunnel', 'receipts', 'openai', 'chatgpt', 'hybs-gateway', 'notifications', 'ssh'

  // ── SSH state ──────────────────────────────────────────────
  const [sshPort, setSshPort] = useState('');
  const [sshNewPort, setSshNewPort] = useState('');
  const [sshSaving, setSshSaving] = useState(false);
  const [sshMessage, setSshMessage] = useState({ type: '', text: '' });
  const [sshLogs, setSshLogs] = useState([]);
  const [sshLogsLoading, setSshLogsLoading] = useState(false);
  const [sshLogsFilter, setSshLogsFilter] = useState('all'); // 'all','failed','accepted','banned'
  const [bannedIPs, setBannedIPs] = useState([]);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [mikrowispConfig, setMikrowispConfig] = useState({
    mikrowispDomain: '',
    mikrowispToken: ''
  });
  const [webhookInfo, setWebhookInfo] = useState({
    webhookUrl: '',
    webhookUrlLocal: '',
    verifyToken: '',
    localIP: '',
    tunnelUrl: null,
    tunnelStatus: 'not_running'
  });
  const [tunnelLoading, setTunnelLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [openaiConfig, setOpenaiConfig] = useState({
    openaiApiKey: '',
    openaiApiKeyExists: false
  });
  const [testingOpenAI, setTestingOpenAI] = useState(false);
  const [chatgptConfig, setChatgptConfig] = useState({
    chatgptEnabledForChat: false,
    chatgptPrompts: []
  });
  const [hybsGatewayConfig, setHybsGatewayConfig] = useState({
    hybsGatewayUrl: '',
    hybsAppkey: '',
    hybsAuthkey: '',
    hybsGatewayEnabled: false,
    hybsCharacterLimit: 160,
    hybsMessageDelay: 3000
  });

  useEffect(() => {
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/config');
      const loadedConfig = response.data.config;
      
      console.log('Config cargada:', loadedConfig); // Debug
      
      // Configuración de WhatsApp
      setConfig({
        whatsappApiUrl: loadedConfig.whatsappApiUrl || 'https://graph.facebook.com/v18.0',
        phoneNumberId: loadedConfig.phoneNumberId || '',
        accessToken: loadedConfig.accessToken || '', // Token completo visible
        accessTokenExists: loadedConfig.accessTokenExists || false,
        verifyToken: loadedConfig.verifyToken || 'mi_token_secreto',
        wabaId: loadedConfig.wabaId || '',
      });

      // Configuración de Mikrowisp
      setMikrowispConfig({
        mikrowispDomain: loadedConfig.mikrowispDomain || '',
        mikrowispToken: loadedConfig.mikrowispToken || ''
      });

      // Información del Webhook/Túnel
      setWebhookInfo({
        webhookUrl: loadedConfig.webhookUrl || '',
        webhookUrlLocal: loadedConfig.webhookUrlLocal || '',
        verifyToken: loadedConfig.verifyToken || '',
        localIP: loadedConfig.localIP || '',
        tunnelUrl: loadedConfig.tunnelUrl || null,
        tunnelStatus: loadedConfig.tunnelStatus || 'not_running'
      });

      // Configuración de OpenAI
      setOpenaiConfig({
        openaiApiKey: loadedConfig.openaiApiKey || '',
        openaiApiKeyExists: loadedConfig.openaiApiKeyExists || false
      });

      // Configuración de ChatGPT para conversaciones
      setChatgptConfig({
        chatgptEnabledForChat: loadedConfig.chatgptEnabledForChat || false,
        chatgptPrompts: loadedConfig.chatgptPrompts || []
      });

      // Configuración del Gateway Hybs
      setHybsGatewayConfig({
        hybsGatewayUrl: loadedConfig.hybsGatewayUrl || '',
        hybsAppkey: loadedConfig.hybsAppkey || '',
        hybsAuthkey: loadedConfig.hybsAuthkey || '',
        hybsGatewayEnabled: loadedConfig.hybsGatewayEnabled || false,
        hybsCharacterLimit: loadedConfig.hybsCharacterLimit || 160,
        hybsMessageDelay: loadedConfig.hybsMessageDelay || 3000
      });
    } catch (error) {
      console.error('Error cargando configuración:', error);
      setMessage({ type: 'error', text: 'Error al cargar la configuración' });
    } finally {
      setLoading(false);
    }
  };

  // ── SSH helpers ──────────────────────────────────────────────
  const loadSshInfo = useCallback(async () => {
    try {
      const res = await axios.get('/api/config/ssh');
      if (res.data.success) {
        setSshPort(res.data.port);
        setSshNewPort(String(res.data.port));
        setBannedIPs(res.data.bannedIPs || []);
      }
    } catch (e) { console.error('SSH info error', e); }
  }, []);

  const loadSshLogs = useCallback(async () => {
    setSshLogsLoading(true);
    try {
      const res = await axios.get('/api/config/ssh/logs?limit=200');
      if (res.data.success) setSshLogs(res.data.logs || []);
    } catch (e) { console.error('SSH logs error', e); }
    finally { setSshLogsLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'ssh') {
      loadSshInfo();
      loadSshLogs();
    }
  }, [activeTab, loadSshInfo, loadSshLogs]);

  const handleSaveSSHPort = async () => {
    const p = parseInt(sshNewPort, 10);
    if (!p || p < 1024 || p > 65535) {
      setSshMessage({ type: 'error', text: 'Puerto inválido. Debe estar entre 1024 y 65535.' });
      return;
    }
    if (!window.confirm(`⚠️ ¿Cambiar SSH de puerto ${sshPort} → ${p}?\nAsegúrate de que el firewall/Hostinger permite el puerto ${p} antes de continuar.`)) return;
    setSshSaving(true);
    setSshMessage({ type: '', text: '' });
    try {
      const res = await axios.put('/api/config/ssh', { port: p });
      if (res.data.success) {
        setSshPort(p);
        setSshMessage({ type: 'success', text: res.data.message });
      } else {
        setSshMessage({ type: 'error', text: res.data.error || 'Error al cambiar puerto' });
      }
    } catch (e) {
      setSshMessage({ type: 'error', text: e.response?.data?.error || 'Error de red' });
    } finally { setSshSaving(false); }
  };
  // ─────────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ type: '', text: '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      let response;
      if (activeTab === 'whatsapp') {
        response = await axios.put('/api/config', config);
        setMessage({ type: 'success', text: response.data.message || 'Configuración de WhatsApp guardada exitosamente' });
      } else if (activeTab === 'mikrowisp') {
        response = await axios.put('/api/config/mikrowisp', mikrowispConfig);
        setMessage({ type: 'success', text: response.data.message || 'Configuración de Mikrowisp guardada exitosamente' });
      } else if (activeTab === 'openai') {
        response = await axios.put('/api/config/openai', { openaiApiKey: openaiConfig.openaiApiKey });
        setMessage({ type: 'success', text: response.data.message || 'Configuración de OpenAI guardada exitosamente' });
        if (response.data.config) {
          setOpenaiConfig(prev => ({
            ...prev,
            openaiApiKeyExists: response.data.config.openaiApiKeyExists
          }));
        }
      } else if (activeTab === 'hybs-gateway') {
        response = await axios.put('/api/config/hybs-gateway', hybsGatewayConfig);
        setMessage({ type: 'success', text: response.data.message || 'Configuración del Gateway Hybs guardada exitosamente' });
      } else if (activeTab === 'chatgpt') {
        response = await axios.put('/api/config/chatgpt', {
          chatgptEnabledForChat: chatgptConfig.chatgptEnabledForChat,
          chatgptPrompts: chatgptConfig.chatgptPrompts
        });
        setMessage({ type: 'success', text: response.data.message || 'Configuración de ChatGPT guardada exitosamente' });
      }
      
      // Recargar después de un momento
      setTimeout(() => {
        loadConfig();
      }, 1000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error al guardar la configuración' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('/api/config/test');
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
      } else {
        setMessage({ type: 'error', text: response.data.error });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error al verificar la configuración' 
      });
    } finally {
      setTesting(false);
    }
  };

  const handleMikrowispChange = (e) => {
    const { name, value } = e.target;
    setMikrowispConfig(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ type: '', text: '' });
  };

  const handleOpenAIChange = (e) => {
    const { name, value } = e.target;
    setOpenaiConfig(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ type: '', text: '' });
  };

  const handleTestOpenAI = async () => {
    setTestingOpenAI(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('/api/config/openai/test', {
        openaiApiKey: openaiConfig.openaiApiKey
      });
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
      } else {
        setMessage({ type: 'error', text: response.data.error });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error al verificar la configuración de OpenAI' 
      });
    } finally {
      setTestingOpenAI(false);
    }
  };

  const handleStartTunnel = async () => {
    setTunnelLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('/api/config/tunnel/start');
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message || 'Túnel creado exitosamente' });
        setWebhookInfo(prev => ({
          ...prev,
          tunnelUrl: response.data.tunnelUrl,
          webhookUrl: response.data.webhookUrl,
          tunnelStatus: 'running'
        }));
        setTimeout(() => {
          loadConfig();
        }, 2000);
      } else {
        const errorMsg = response.data.error || 'Error desconocido';
        const instructions = response.data.instructions || '';
        setMessage({ 
          type: 'error', 
          text: errorMsg + (instructions ? '\n\n' + instructions : '') 
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      const instructions = error.response?.data?.instructions || '';
      setMessage({ 
        type: 'error', 
        text: 'Error al iniciar túnel: ' + errorMsg + (instructions ? '\n\n' + instructions : '') 
      });
    } finally {
      setTunnelLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="config-modal-overlay" onClick={onClose}>
      <div className="config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="config-modal-header">
          <h2>⚙️ Configuración</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Pestañas */}
        <div className="config-tabs">
          <button
            type="button"
            className={`config-tab ${activeTab === 'whatsapp' ? 'active' : ''}`}
            onClick={() => setActiveTab('whatsapp')}
          >
            📱 WhatsApp
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === 'mikrowisp' ? 'active' : ''}`}
            onClick={() => setActiveTab('mikrowisp')}
          >
            🔗 Mikrowisp
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === 'tunnel' ? 'active' : ''}`}
            onClick={() => setActiveTab('tunnel')}
          >
            🌐 Túnel
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === 'receipts' ? 'active' : ''}`}
            onClick={() => setActiveTab('receipts')}
          >
            🧾 Modelos de Recibos
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === 'openai' ? 'active' : ''}`}
            onClick={() => setActiveTab('openai')}
          >
            🤖 OpenAI / ChatGPT
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === 'chatgpt' ? 'active' : ''}`}
            onClick={() => setActiveTab('chatgpt')}
          >
            💬 ChatGPT Conversaciones
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === 'hybs-gateway' ? 'active' : ''}`}
            onClick={() => setActiveTab('hybs-gateway')}
          >
            📡 Gateway Hybs
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('notifications');
              if ('Notification' in window) {
                setNotificationPermission(Notification.permission);
              }
            }}
          >
            🔔 Notificaciones
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === 'ssh' ? 'active' : ''}`}
            onClick={() => setActiveTab('ssh')}
          >
            🔐 Servidor SSH
          </button>
        </div>

        {/* Contenido de las pestañas */}
        {activeTab === 'whatsapp' && (
          <form onSubmit={handleSave} className="config-form">
          {message.text && (
            <div className={`config-message ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="config-section">
            <h3>Credenciales de API</h3>
            <p className="config-help">
              Obtén estas credenciales desde tu cuenta de Meta for Developers
            </p>

            <div className="form-group">
              <label htmlFor="whatsappApiUrl">URL de la API</label>
              <input
                type="text"
                id="whatsappApiUrl"
                name="whatsappApiUrl"
                value={config.whatsappApiUrl}
                onChange={handleChange}
                placeholder="https://graph.facebook.com/v18.0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumberId">
                Identificador del número de teléfono <span className="required">*</span>
              </label>
              <input
                type="text"
                id="phoneNumberId"
                name="phoneNumberId"
                value={config.phoneNumberId}
                onChange={handleChange}
                placeholder="123456789012345"
                required
              />
              <small className="form-help">
                Encuéntralo en Meta for Developers → Tu App → WhatsApp → Configuración
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="accessToken">
                Token de acceso (Access Token) <span className="required">*</span>
                {config.accessTokenExists && (
                  <span className="token-exists-badge">✓ Guardado</span>
                )}
              </label>
              <input
                type="text"
                id="accessToken"
                name="accessToken"
                value={config.accessToken}
                onChange={handleChange}
                placeholder={config.accessTokenExists ? "Token guardado (ingresa nuevo token para cambiar)" : "EAAxxxxxxxxxxxxx (ingresa el token completo)"}
                required={!config.accessTokenExists}
              />
              <small className="form-help">
                {config.accessTokenExists 
                  ? "Token guardado. Solo ingresa un nuevo token si deseas cambiarlo. El token actual permanecerá guardado."
                  : "Token permanente o temporal de acceso. Guárdalo de forma segura."}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="wabaId">
                WhatsApp Business Account ID (WABA ID) <span className="required">*</span>
              </label>
              <input
                type="text"
                id="wabaId"
                name="wabaId"
                value={config.wabaId}
                onChange={handleChange}
                placeholder="123456789012345"
              />
              <small className="form-help">
                <strong>Requerido para cargar plantillas automáticamente.</strong><br />
                Encuéntralo en Meta for Developers → Tu App → WhatsApp → Configuración → WhatsApp Business Account ID
                <br />
                O en: Tu App → Configuración → WhatsApp → Número de teléfono → Ver detalles
              </small>
            </div>

          </div>

          <div className="config-actions">
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || !config.phoneNumberId || !config.accessToken}
              className="test-button"
            >
              {testing ? 'Verificando...' : '🔍 Verificar Conexión'}
            </button>
            <div className="action-buttons">
              <button
                type="button"
                onClick={onClose}
                className="cancel-button"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving || !config.phoneNumberId || !config.accessToken}
                className="save-button"
              >
                {saving ? 'Guardando...' : '💾 Guardar Configuración'}
              </button>
            </div>
          </div>
        </form>
        )}

        {activeTab === 'mikrowisp' && (
          <form onSubmit={handleSave} className="config-form">
            {message.text && (
              <div className={`config-message ${message.type}`}>
                {message.text}
              </div>
            )}

            {loading ? (
              <div className="config-loading">Cargando configuración...</div>
            ) : (
              <div className="config-section">
                <h3>🔗 Configuración de Mikrowisp</h3>
                <p className="config-help">
                  Configura la conexión con Mikrowisp para sincronizar contactos automáticamente
                </p>

                <div className="form-group">
                  <label htmlFor="mikrowispDomain">
                    URL del Servidor Mikrowisp
                  </label>
                  <input
                    type="text"
                    id="mikrowispDomain"
                    name="mikrowispDomain"
                    value={mikrowispConfig.mikrowispDomain}
                    onChange={handleMikrowispChange}
                    placeholder="http://179.49.202.35:8585"
                  />
                  <small className="form-help">
                    URL completa del servidor Mikrowisp (incluyendo http:// y puerto)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="mikrowispToken">
                    Token de API de Mikrowisp
                  </label>
                  <input
                    type="text"
                    id="mikrowispToken"
                    name="mikrowispToken"
                    value={mikrowispConfig.mikrowispToken}
                    onChange={handleMikrowispChange}
                    placeholder="Ingresa el token de API"
                  />
                  <small className="form-help">
                    Token de autenticación para acceder a la API de Mikrowisp
                  </small>
                </div>
              </div>
            )}

            <div className="config-actions">
              <div className="action-buttons">
                <button
                  type="button"
                  onClick={onClose}
                  className="cancel-button"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={saving || loading}
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </form>
        )}

        {activeTab === 'tunnel' && (
          <div className="config-form">
            {message.text && (
              <div className={`config-message ${message.type}`}>
                {message.text}
              </div>
            )}

            {loading ? (
              <div className="config-loading">Cargando configuración...</div>
            ) : (
              <div className="config-section">
                <h3>🌐 Configuración del Túnel (ngrok)</h3>
                <p className="config-help">
                  Configura el túnel público para recibir webhooks de WhatsApp
                </p>

                <div className="form-group">
                  <label>🌐 Acceso Remoto al Panel</label>
                  {webhookInfo.tunnelUrl ? (
                    <div className="webhook-url-display">
                      <input
                        type="text"
                        className="readonly-input"
                        value={webhookInfo.tunnelUrl}
                        readOnly
                      />
                      <button
                        type="button"
                        className="copy-button"
                        onClick={() => {
                          navigator.clipboard.writeText(webhookInfo.tunnelUrl);
                          setMessage({ type: 'success', text: 'URL copiada al portapapeles' });
                        }}
                        title="Copiar URL"
                      >
                        📋 Copiar
                      </button>
                      <a
                        href={webhookInfo.tunnelUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="open-link-button"
                        style={{
                          marginLeft: '10px',
                          padding: '8px 16px',
                          backgroundColor: '#25D366',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '14px',
                          display: 'inline-block'
                        }}
                      >
                        🔗 Abrir Panel
                      </a>
                    </div>
                  ) : (
                    <div className="form-help" style={{ color: '#e74c3c', padding: '10px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
                      ⚠️ El túnel no está activo. Inicia el túnel para acceder al panel remotamente.
                    </div>
                  )}
                  <small className="form-help">
                    Usa esta URL para acceder al panel desde cualquier navegador (computadora, móvil, etc.)
                  </small>
                </div>

                <div className="form-group">
                  <label>URL del Webhook (Público)</label>
                  <div className="webhook-url-display">
                    <input
                      type="text"
                      className="readonly-input"
                      value={webhookInfo.webhookUrl || 'No configurado'}
                      readOnly
                    />
                    <button
                      type="button"
                      className="copy-button"
                      onClick={() => {
                        navigator.clipboard.writeText(webhookInfo.webhookUrl);
                        setMessage({ type: 'success', text: 'URL copiada al portapapeles' });
                      }}
                      title="Copiar URL"
                    >
                      📋 Copiar
                    </button>
                  </div>
                  <small className="form-help">
                    URL pública para configurar el webhook en Meta for Developers
                  </small>
                </div>

                <div className="form-group">
                  <label>URL del Webhook (Local)</label>
                  <div className="webhook-url-display">
                    <input
                      type="text"
                      className="readonly-input"
                      value={webhookInfo.webhookUrlLocal || 'No configurado'}
                      readOnly
                    />
                    <button
                      type="button"
                      className="copy-button"
                      onClick={() => {
                        navigator.clipboard.writeText(webhookInfo.webhookUrlLocal);
                        setMessage({ type: 'success', text: 'URL copiada al portapapeles' });
                      }}
                      title="Copiar URL"
                    >
                      📋 Copiar
                    </button>
                  </div>
                  <small className="form-help">
                    URL local del servidor (solo accesible en tu red)
                  </small>
                </div>

                <div className="form-group">
                  <label>Token de Verificación del Webhook</label>
                  <div className="webhook-token-display">
                    <input
                      type="text"
                      className="readonly-input"
                      value={webhookInfo.verifyToken || 'Cargando...'}
                      readOnly
                    />
                    <button
                      type="button"
                      className="copy-button"
                      onClick={async () => {
                        try {
                          const response = await axios.get('/api/config/verify-token');
                          const fullToken = response.data.verifyToken;
                          if (fullToken) {
                            await navigator.clipboard.writeText(fullToken);
                            setMessage({ type: 'success', text: 'Token copiado al portapapeles' });
                          }
                        } catch (error) {
                          console.error('Error copiando token:', error);
                          setMessage({ type: 'error', text: 'Error al copiar token' });
                        }
                      }}
                      title="Copiar token completo"
                    >
                      📋 Copiar
                    </button>
                  </div>
                  <small className="form-help">
                    Este token se genera automáticamente. Úsalo como "Verify Token" en Meta for Developers
                  </small>
                </div>

                <div className="form-group">
                  <label>Estado del Túnel</label>
                  <div className="tunnel-controls">
                    <button
                      type="button"
                      className={`tunnel-button ${webhookInfo.tunnelStatus === 'running' ? 'tunnel-active' : ''}`}
                      onClick={handleStartTunnel}
                      disabled={tunnelLoading || webhookInfo.tunnelStatus === 'running'}
                    >
                      {tunnelLoading ? 'Iniciando...' : webhookInfo.tunnelStatus === 'running' ? '✅ Túnel Activo' : '🚇 Iniciar Túnel ngrok'}
                    </button>
                    {webhookInfo.tunnelStatus === 'running' && webhookInfo.tunnelUrl && (
                      <div className="local-url-info">
                        <strong>✅ Túnel activo:</strong> <code>{webhookInfo.tunnelUrl}</code>
                      </div>
                    )}
                    {webhookInfo.tunnelStatus === 'not_installed' && (
                      <div className="form-help" style={{ color: '#e74c3c' }}>
                        ⚠️ ngrok no está instalado. Instálalo desde <a href="https://ngrok.com/download" target="_blank" rel="noopener noreferrer">ngrok.com</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="config-actions">
              <div className="action-buttons">
                <button
                  type="button"
                  onClick={onClose}
                  className="cancel-button"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'receipts' && (
          <ReceiptModelsTab />
        )}

        {activeTab === 'openai' && (
          <form onSubmit={handleSave} className="config-form">
            {message.text && (
              <div className={`config-message ${message.type}`}>
                {message.text}
              </div>
            )}

            {loading ? (
              <div className="config-loading">Cargando configuración...</div>
            ) : (
              <div className="config-section">
                <h3>🤖 Configuración de OpenAI / ChatGPT</h3>
                <p className="config-help">
                  Configura la API de OpenAI para usar GPT-4 Vision en la detección automática de recibos bancarios.
                  Esto mejora significativamente la precisión de extracción de datos.
                </p>

                <div className="form-group">
                  <label htmlFor="openaiApiKey">
                    API Key de OpenAI
                    {openaiConfig.openaiApiKeyExists && (
                      <span className="token-exists-badge">✓ Guardada</span>
                    )}
                  </label>
                  <input
                    type="password"
                    id="openaiApiKey"
                    name="openaiApiKey"
                    value={openaiConfig.openaiApiKey}
                    onChange={handleOpenAIChange}
                    placeholder={openaiConfig.openaiApiKeyExists ? "API Key guardada (ingresa nueva key para cambiar)" : "sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
                  />
                  <small className="form-help">
                    {openaiConfig.openaiApiKeyExists 
                      ? "API Key guardada. Solo ingresa una nueva key si deseas cambiarla. La key actual permanecerá guardada."
                      : "Obtén tu API Key desde https://platform.openai.com/api-keys"}
                    <br />
                    <strong>Nota:</strong> La API Key se usa para analizar recibos bancarios con GPT-4 Vision.
                    Si no configuras una API Key, el sistema usará OCR tradicional como fallback.
                  </small>
                </div>

                <div className="form-group">
                  <div className="info-box" style={{
                    padding: '12px',
                    backgroundColor: '#e7f3ff',
                    border: '1px solid #b3d9ff',
                    borderRadius: '6px',
                    marginTop: '10px'
                  }}>
                    <strong>💡 Información:</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      <li>GPT-4 Vision mejora la precisión de detección de recibos</li>
                      <li>Costo aproximado: ~$0.01 por recibo analizado</li>
                      <li>Si no configuras la API Key, el sistema usará OCR gratuito</li>
                      <li>La API Key se guarda de forma segura en el servidor</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="config-actions">
              <button
                type="button"
                onClick={handleTestOpenAI}
                disabled={testingOpenAI || !openaiConfig.openaiApiKey}
                className="test-button"
              >
                {testingOpenAI ? 'Verificando...' : '🔍 Verificar API Key'}
              </button>
              <div className="action-buttons">
                <button
                  type="button"
                  onClick={onClose}
                  className="cancel-button"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={saving || loading}
                >
                  {saving ? 'Guardando...' : '💾 Guardar Configuración'}
                </button>
              </div>
            </div>
          </form>
        )}

        {activeTab === 'chatgpt' && (
          <ChatGPTConversationsTab
            chatgptConfig={chatgptConfig}
            setChatgptConfig={setChatgptConfig}
            message={message}
            setMessage={setMessage}
            saving={saving}
            onClose={onClose}
            handleSave={handleSave}
          />
        )}

        {/* ═══════════════ TAB SSH ═══════════════ */}
        {activeTab === 'ssh' && (
          <div className="config-form">
            {/* ── Puerto SSH ── */}
            <div className="config-section">
              <h3>🔐 Puerto SSH del Servidor</h3>
              <p className="config-help">
                Puerto actual: <strong>{sshPort || '…'}</strong>. Cambia el puerto y haz clic en "Aplicar".
                Asegúrate de abrir el nuevo puerto en Hostinger antes de guardar.
              </p>
              {sshMessage.text && (
                <div className={`config-message ${sshMessage.type}`}>{sshMessage.text}</div>
              )}
              <div className="form-group" style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '0 0 140px' }}>
                  <label>Puerto SSH</label>
                  <input
                    type="number"
                    min="1024" max="65535"
                    value={sshNewPort}
                    onChange={(e) => setSshNewPort(e.target.value)}
                    placeholder="ej: 2226"
                    style={{ width: '100%' }}
                  />
                </div>
                <button
                  type="button"
                  className="save-button"
                  style={{ marginBottom: 0 }}
                  disabled={sshSaving || String(sshNewPort) === String(sshPort)}
                  onClick={handleSaveSSHPort}
                >
                  {sshSaving ? 'Aplicando…' : '⚡ Aplicar'}
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  style={{ marginBottom: 0 }}
                  onClick={loadSshInfo}
                >
                  🔄 Refrescar
                </button>
              </div>

              {bannedIPs.length > 0 && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#fff3cd', borderRadius: 8, border: '1px solid #f0ad4e' }}>
                  <strong>🚫 IPs baneadas por fail2ban:</strong>
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {bannedIPs.map(ip => (
                      <span key={ip} style={{ background: '#dc3545', color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>{ip}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Log de intentos SSH ── */}
            <div className="config-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ margin: 0 }}>📋 Log de Intentos de Conexión</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={sshLogsFilter}
                    onChange={(e) => setSshLogsFilter(e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="all">Todos</option>
                    <option value="accepted">✅ Aceptados</option>
                    <option value="failed">❌ Fallidos</option>
                    <option value="banned">🚫 Baneados</option>
                    <option value="invalid">⚠️ Usuario inválido</option>
                    <option value="scan">🔍 Escaneos</option>
                  </select>
                  <button
                    type="button"
                    className="cancel-button"
                    style={{ padding: '4px 12px', marginBottom: 0 }}
                    onClick={loadSshLogs}
                    disabled={sshLogsLoading}
                  >
                    {sshLogsLoading ? '…' : '🔄'}
                  </button>
                </div>
              </div>

              {sshLogsLoading && <p style={{ color: '#888', marginTop: 12 }}>Cargando logs…</p>}

              {!sshLogsLoading && sshLogs.length === 0 && (
                <p style={{ color: '#888', marginTop: 12 }}>No se encontraron entradas en auth.log / journalctl.</p>
              )}

              {!sshLogsLoading && sshLogs.length > 0 && (() => {
                const filtered = sshLogsFilter === 'all'
                  ? sshLogs
                  : sshLogs.filter(l => l.action === sshLogsFilter);
                const actionBadge = (a) => {
                  const map = {
                    accepted: { bg: '#28a745', label: '✅ Aceptado' },
                    failed:   { bg: '#dc3545', label: '❌ Fallido' },
                    invalid:  { bg: '#fd7e14', label: '⚠️ Inválido' },
                    banned:   { bg: '#6f42c1', label: '🚫 Baneado' },
                    scan:     { bg: '#6c757d', label: '🔍 Escaneo' },
                  };
                  const s = map[a] || { bg: '#999', label: a };
                  return (
                    <span style={{ background: s.bg, color: '#fff', borderRadius: 4, padding: '2px 7px', fontSize: 11, whiteSpace: 'nowrap' }}>
                      {s.label}
                    </span>
                  );
                };
                return (
                  <div style={{ overflowX: 'auto', marginTop: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f1f3f5', textAlign: 'left' }}>
                          <th style={{ padding: '6px 8px', borderBottom: '2px solid #dee2e6' }}>Fecha / Hora</th>
                          <th style={{ padding: '6px 8px', borderBottom: '2px solid #dee2e6' }}>IP Pública</th>
                          <th style={{ padding: '6px 8px', borderBottom: '2px solid #dee2e6' }}>Puerto</th>
                          <th style={{ padding: '6px 8px', borderBottom: '2px solid #dee2e6' }}>Usuario</th>
                          <th style={{ padding: '6px 8px', borderBottom: '2px solid #dee2e6' }}>Estado</th>
                          <th style={{ padding: '6px 8px', borderBottom: '2px solid #dee2e6' }}>Vía</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((log, i) => (
                          <tr
                            key={i}
                            style={{
                              background: log.action === 'accepted' ? '#f0fff4'
                                        : log.action === 'banned'   ? '#f8f0ff'
                                        : log.action === 'failed'   ? '#fff5f5'
                                        : i % 2 === 0 ? '#fff' : '#fafafa',
                              borderBottom: '1px solid #f0f0f0'
                            }}
                          >
                            <td style={{ padding: '5px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{log.time || '—'}</td>
                            <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontWeight: 600, color: log.action === 'accepted' ? '#155724' : log.action === 'banned' ? '#6f42c1' : '#b02020' }}>
                              {log.ip || '—'}
                            </td>
                            <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{log.port || '—'}</td>
                            <td style={{ padding: '5px 8px' }}>{log.user || '—'}</td>
                            <td style={{ padding: '5px 8px' }}>{actionBadge(log.action)}</td>
                            <td style={{ padding: '5px 8px', color: '#555' }}>{log.via || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p style={{ marginTop: 8, color: '#888', fontSize: 11 }}>
                      Mostrando {filtered.length} de {sshLogs.length} entradas.
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        {/* ═══════════════ FIN TAB SSH ═══════════════ */}

        {activeTab === 'notifications' && (
          <div className="config-form">
            <div className="config-section">
              <h3>🔔 Notificaciones del Navegador</h3>
              <p className="config-help">
                Configura las notificaciones del navegador para recibir alertas cuando los clientes envíen mensajes.
              </p>

              <div className="form-group">
                <label>Estado de los Permisos</label>
                <div style={{
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: notificationPermission === 'granted' ? '#d4edda' : 
                                  notificationPermission === 'denied' ? '#f8d7da' : '#fff3cd',
                  color: notificationPermission === 'granted' ? '#155724' : 
                         notificationPermission === 'denied' ? '#721c24' : '#856404',
                  marginBottom: '15px',
                  border: `1px solid ${notificationPermission === 'granted' ? '#c3e6cb' : 
                                          notificationPermission === 'denied' ? '#f5c6cb' : '#ffeaa7'}`
                }}>
                  {notificationPermission === 'granted' && (
                    <div>
                      <strong>✅ Permisos Otorgados</strong>
                      <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                        Recibirás notificaciones cuando los clientes envíen mensajes, incluso si la ventana no está enfocada.
                      </p>
                    </div>
                  )}
                  {notificationPermission === 'denied' && (
                    <div>
                      <strong>❌ Permisos Denegados</strong>
                      <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                        Los permisos de notificaciones fueron denegados. Para habilitarlos:
                      </p>
                      <ol style={{ margin: '8px 0 0 20px', fontSize: '14px' }}>
                        <li>Haz clic en el ícono de candado 🔒 en la barra de direcciones de Chrome</li>
                        <li>Busca "Notificaciones" en la lista</li>
                        <li>Cambia de "Bloquear" a "Permitir"</li>
                        <li>Recarga la página</li>
                      </ol>
                    </div>
                  )}
                  {notificationPermission === 'default' && (
                    <div>
                      <strong>⚠️ Permisos No Solicitados</strong>
                      <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                        Haz clic en el botón de abajo para solicitar permisos de notificaciones.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <button
                  type="button"
                  onClick={async () => {
                    if (!('Notification' in window)) {
                      setMessage({ 
                        type: 'error', 
                        text: 'Tu navegador no soporta notificaciones' 
                      });
                      return;
                    }

                    if (Notification.permission === 'denied') {
                      setMessage({ 
                        type: 'error', 
                        text: 'Los permisos fueron denegados. Por favor, habilítalos manualmente desde la configuración del navegador (ícono de candado en la barra de direcciones).' 
                      });
                      return;
                    }

                    const granted = await requestNotificationPermission();
                    setNotificationPermission(Notification.permission);

                    if (granted) {
                      setMessage({ 
                        type: 'success', 
                        text: '✅ Permisos otorgados. Ahora recibirás notificaciones cuando los clientes envíen mensajes.' 
                      });
                      
                      // Mostrar una notificación de prueba
                      setTimeout(() => {
                        showNotification('Notificaciones Activadas', {
                          body: 'Ahora recibirás alertas cuando los clientes envíen mensajes.',
                          tag: 'test-notification'
                        });
                      }, 500);
                    } else {
                      setMessage({ 
                        type: 'error', 
                        text: 'Los permisos de notificaciones fueron denegados. Por favor, habilítalos desde la configuración del navegador.' 
                      });
                    }
                  }}
                  disabled={notificationPermission === 'denied'}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: notificationPermission === 'denied' ? '#ccc' : '#00a884',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: notificationPermission === 'denied' ? 'not-allowed' : 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                >
                  {notificationPermission === 'granted' 
                    ? '✅ Permisos Otorgados' 
                    : notificationPermission === 'denied'
                    ? '❌ Permisos Denegados (Habilitar Manualmente)'
                    : '🔔 Solicitar Permisos de Notificaciones'}
                </button>
              </div>

              <div className="form-group" style={{ marginTop: '30px' }}>
                <h4 style={{ marginBottom: '15px' }}>📋 Información</h4>
                <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                  <li>Las notificaciones solo aparecen cuando la ventana del panel no está enfocada</li>
                  <li>Al hacer clic en una notificación, se abrirá la ventana del panel</li>
                  <li>Las notificaciones incluyen el nombre del cliente y un preview del mensaje</li>
                  <li>También se reproducirá un sonido cuando llegue un mensaje nuevo</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'hybs-gateway' && (
          <form onSubmit={handleSave} className="config-form">
            {message.text && (
              <div className={`config-message ${message.type}`}>
                {message.text}
              </div>
            )}

            <div className="config-section">
              <h3>🔧 Configuración del Gateway Hybs</h3>
              <p className="config-description">
                Configura el gateway alternativo de WhatsApp para enviar mensajes desde Mikrowisp.
                El endpoint para recibir peticiones de Mikrowisp es: <code>/api/messages/send-from-mikrowisp</code>
              </p>

              <div className="config-field">
                <label>
                  <input
                    type="checkbox"
                    checked={hybsGatewayConfig.hybsGatewayEnabled}
                    onChange={(e) => setHybsGatewayConfig(prev => ({
                      ...prev,
                      hybsGatewayEnabled: e.target.checked
                    }))}
                  />
                  <span>Activar Gateway</span>
                </label>
              </div>

              <div className="config-field">
                <label htmlFor="hybsGatewayUrl">URL Gateway:</label>
                <input
                  type="url"
                  id="hybsGatewayUrl"
                  name="hybsGatewayUrl"
                  value={hybsGatewayConfig.hybsGatewayUrl}
                  onChange={(e) => setHybsGatewayConfig(prev => ({
                    ...prev,
                    hybsGatewayUrl: e.target.value
                  }))}
                  placeholder="https://api.hybs.pe/api/create-message"
                  required={hybsGatewayConfig.hybsGatewayEnabled}
                />
              </div>

              <div className="config-field">
                <label htmlFor="hybsAppkey">App Key:</label>
                <input
                  type="text"
                  id="hybsAppkey"
                  name="hybsAppkey"
                  value={hybsGatewayConfig.hybsAppkey}
                  onChange={(e) => setHybsGatewayConfig(prev => ({
                    ...prev,
                    hybsAppkey: e.target.value
                  }))}
                  placeholder="ab82c2ee-b53c-4a45-ad56-e3567d974 (Opcional)"
                />
              </div>

              <div className="config-field">
                <label htmlFor="hybsAuthkey">Auth Key:</label>
                <input
                  type="text"
                  id="hybsAuthkey"
                  name="hybsAuthkey"
                  value={hybsGatewayConfig.hybsAuthkey}
                  onChange={(e) => setHybsGatewayConfig(prev => ({
                    ...prev,
                    hybsAuthkey: e.target.value
                  }))}
                  placeholder="(Opcional - dejar vacío si no se requiere)"
                />
              </div>

              <div className="config-field">
                <label htmlFor="hybsCharacterLimit">Límite de Caracteres:</label>
                <input
                  type="number"
                  id="hybsCharacterLimit"
                  name="hybsCharacterLimit"
                  value={hybsGatewayConfig.hybsCharacterLimit}
                  onChange={(e) => setHybsGatewayConfig(prev => ({
                    ...prev,
                    hybsCharacterLimit: parseInt(e.target.value) || 160
                  }))}
                  min="1"
                  max="10000"
                />
                <small>Cantidad máxima de caracteres por mensaje (recomendado: 160 según Mikrowisp)</small>
              </div>

              <div className="config-field">
                <label htmlFor="hybsMessageDelay">Pausa Entre Mensajes (ms):</label>
                <input
                  type="number"
                  id="hybsMessageDelay"
                  name="hybsMessageDelay"
                  value={hybsGatewayConfig.hybsMessageDelay}
                  onChange={(e) => setHybsGatewayConfig(prev => ({
                    ...prev,
                    hybsMessageDelay: parseInt(e.target.value) || 3000
                  }))}
                  min="0"
                  max="10000"
                />
                <small>Tiempo de espera entre mensajes (recomendado: 3000ms = 3 segundos según Mikrowisp)</small>
              </div>

              <div className="config-info-box">
                <h4>📋 Configuración para Mikrowisp - Gateway Genérico:</h4>
                <p style={{ marginBottom: '20px', color: '#666' }}>
                  Ingresa estos datos en Mikrowisp → Ajustes → SMS → Gateway Genérico
                </p>

                {/* URL Gateway */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#004085' }}>
                    1. URL Gateway (con parámetros en query string):
                  </label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <code style={{ 
                      flex: 1, 
                      padding: '10px', 
                      backgroundColor: 'white', 
                      borderRadius: '4px',
                      wordBreak: 'break-all',
                      color: '#004085',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}>
                      {(() => {
                        const baseUrl = webhookInfo.tunnelUrl && webhookInfo.tunnelStatus === 'running'
                          ? webhookInfo.tunnelUrl
                          : webhookInfo.localIP 
                            ? `http://${webhookInfo.localIP}:5000`
                            : webhookInfo.webhookUrlLocal?.replace('/api/webhook', '').replace(':5000', '') || window.location.origin.replace(':3000', ':5000');
                        return `${baseUrl}/api/mikrotik-messages?to={destinatario}&message={mensaje}`;
                      })()}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        const baseUrl = webhookInfo.tunnelUrl && webhookInfo.tunnelStatus === 'running'
                          ? webhookInfo.tunnelUrl
                          : webhookInfo.localIP 
                            ? `http://${webhookInfo.localIP}:5000`
                            : webhookInfo.webhookUrlLocal?.replace('/api/webhook', '').replace(':5000', '') || window.location.origin.replace(':3000', ':5000');
                        const url = `${baseUrl}/api/mikrotik-messages?to={destinatario}&message={mensaje}`;
                        navigator.clipboard.writeText(url);
                        setMessage({ type: 'success', text: 'URL Gateway copiada al portapapeles' });
                        setTimeout(() => setMessage({ type: '', text: '' }), 2000);
                      }}
                      style={{
                        padding: '10px 15px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      📋 Copiar
                    </button>
                  </div>
                  <small style={{ display: 'block', marginTop: '5px', color: '#004085' }}>
                    ⚠️ <strong>IMPORTANTE:</strong> Los placeholders <code>{'{destinatario}'}</code> y <code>{'{mensaje}'}</code> deben estar exactamente así, con llaves. Mikrowisp los reemplazará automáticamente.
                  </small>
                  {webhookInfo.localIP && (
                    <small style={{ display: 'block', marginTop: '5px', color: '#004085' }}>
                      ✅ IP Local detectada: <strong>{webhookInfo.localIP}</strong>
                    </small>
                  )}
                  {webhookInfo.tunnelUrl && webhookInfo.tunnelStatus === 'running' && (
                    <small style={{ display: 'block', marginTop: '5px', color: '#004085' }}>
                      ✅ Túnel activo - URL pública disponible
                    </small>
                  )}
                </div>

                {/* Parámetros */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#856404' }}>
                    2. Parámetros:
                  </label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <code style={{ 
                      flex: 1, 
                      padding: '10px', 
                      backgroundColor: 'white', 
                      borderRadius: '4px',
                      wordBreak: 'break-all',
                      color: '#856404',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      fontStyle: 'italic'
                    }}>
                      (DEBE ESTAR VACÍO - los parámetros van en la URL Gateway)
                    </code>
                  </div>
                  <small style={{ display: 'block', marginTop: '5px', color: '#856404' }}>
                    ⚠️ <strong>IMPORTANTE:</strong> Si el campo "Parámetros" tiene contenido, Mikrowisp puede duplicar o malformar la URL. Déjalo completamente vacío.
                  </small>
                </div>

                {/* Método */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    3. Método:
                  </label>
                  <code style={{ 
                    padding: '8px 15px', 
                    backgroundColor: 'white', 
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#28a745'
                  }}>
                    GET (recomendado) o POST
                  </code>
                  <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                    GET funciona mejor con Mikrowisp porque los parámetros van directamente en la URL
                  </small>
                </div>

                {/* Token Authorization Bearer */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    4. Token Authorization Bearer:
                  </label>
                  <code style={{ 
                    padding: '8px 15px', 
                    backgroundColor: 'white', 
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    color: '#6c757d',
                    fontStyle: 'italic'
                  }}>
                    (Dejar vacío)
                  </code>
                </div>

                {/* Límite de caracteres */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    5. Límite de Caracteres:
                  </label>
                  <code style={{ 
                    padding: '8px 15px', 
                    backgroundColor: 'white', 
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#007bff'
                  }}>
                    {hybsGatewayConfig.hybsCharacterLimit || 160}
                  </code>
                </div>

                {/* Pausa entre mensajes */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    6. Pausa Entre Mensajes:
                  </label>
                  <code style={{ 
                    padding: '8px 15px', 
                    backgroundColor: 'white', 
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#007bff'
                  }}>
                    {hybsGatewayConfig.hybsMessageDelay || 3000} ms
                  </code>
                </div>

                {/* Activar Gateway */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#d4edda', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#155724' }}>
                    7. Activar Gateway:
                  </label>
                  <p style={{ margin: 0, color: '#155724' }}>
                    ✅ Activa el toggle "Activar Gateway" en Mikrowisp y guarda la configuración
                  </p>
                </div>

                {/* Resumen visual */}
                <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '2px solid #dee2e6' }}>
                  <h5 style={{ marginTop: 0, marginBottom: '15px', color: '#495057' }}>📝 Resumen de Configuración:</h5>
                  <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px' }}>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>URL Gateway (con parámetros en query string):</strong><br/>
                      <span style={{ color: '#007bff', wordBreak: 'break-all' }}>
                        {(() => {
                          const baseUrl = webhookInfo.tunnelUrl && webhookInfo.tunnelStatus === 'running'
                            ? webhookInfo.tunnelUrl
                            : webhookInfo.localIP 
                              ? `http://${webhookInfo.localIP}:5000`
                              : webhookInfo.webhookUrlLocal?.replace('/api/webhook', '').replace(':5000', '') || window.location.origin.replace(':3000', ':5000');
                          return `${baseUrl}/api/mikrotik-messages?to={destinatario}&message={mensaje}`;
                        })()}
                      </span>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Parámetros:</strong><br/>
                      <span style={{ color: '#856404', fontStyle: 'italic' }}>
                        (VACÍO - los parámetros van en la URL Gateway)
                      </span>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Método:</strong> <span style={{ color: '#28a745' }}>GET (recomendado) o POST</span>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Token Authorization Bearer:</strong> <span style={{ color: '#6c757d', fontStyle: 'italic' }}>(vacío)</span>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Límite de Caracteres:</strong> <span style={{ color: '#007bff' }}>{hybsGatewayConfig.hybsCharacterLimit || 160}</span>
                    </div>
                    <div>
                      <strong>Pausa Entre Mensajes:</strong> <span style={{ color: '#007bff' }}>{hybsGatewayConfig.hybsMessageDelay || 3000} ms</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="config-actions">
                <button
                  type="submit"
                  className="save-button"
                  disabled={saving}
                >
                  {saving ? 'Guardando...' : '💾 Guardar Configuración'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Componente para gestionar ChatGPT Conversaciones
const ChatGPTConversationsTab = ({ chatgptConfig, setChatgptConfig, message, setMessage, saving, onClose, handleSave }) => {
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [newPrompt, setNewPrompt] = useState({ name: '', prompt: '' });

  const handleAddPrompt = async () => {
    if (!newPrompt.name.trim() || !newPrompt.prompt.trim()) {
      setMessage({ type: 'error', text: 'El nombre y el prompt son requeridos' });
      return;
    }

    try {
      const response = await axios.post('/api/config/chatgpt/prompts', {
        name: newPrompt.name,
        prompt: newPrompt.prompt,
        active: true
      });

      setChatgptConfig(prev => ({
        ...prev,
        chatgptPrompts: [...prev.chatgptPrompts, response.data.prompt]
      }));

      setNewPrompt({ name: '', prompt: '' });
      setMessage({ type: 'success', text: 'Prompt agregado exitosamente' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Error al agregar prompt' });
    }
  };

  const handleUpdatePrompt = async (id, updates) => {
    try {
      const response = await axios.put(`/api/config/chatgpt/prompts/${id}`, updates);
      
      setChatgptConfig(prev => ({
        ...prev,
        chatgptPrompts: prev.chatgptPrompts.map(p => 
          p.id === id ? response.data.prompt : p
        )
      }));

      setEditingPrompt(null);
      setMessage({ type: 'success', text: 'Prompt actualizado exitosamente' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Error al actualizar prompt' });
    }
  };

  const handleDeletePrompt = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este prompt?')) {
      return;
    }

    try {
      await axios.delete(`/api/config/chatgpt/prompts/${id}`);
      
      setChatgptConfig(prev => ({
        ...prev,
        chatgptPrompts: prev.chatgptPrompts.filter(p => p.id !== id)
      }));

      setMessage({ type: 'success', text: 'Prompt eliminado exitosamente' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Error al eliminar prompt' });
    }
  };

  const togglePromptActive = async (id, active) => {
    await handleUpdatePrompt(id, { active: !active });
  };

  return (
    <form onSubmit={handleSave} className="config-form">
      {message.text && (
        <div className={`config-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="config-section">
        <h3>💬 ChatGPT para Conversaciones con Clientes</h3>
        <p className="config-help">
          Configura ChatGPT para responder automáticamente a los mensajes de los clientes.
          <strong> Nota:</strong> El procesamiento de recibos siempre usará ChatGPT si está disponible, independientemente de esta configuración.
        </p>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={chatgptConfig.chatgptEnabledForChat}
              onChange={(e) => setChatgptConfig(prev => ({
                ...prev,
                chatgptEnabledForChat: e.target.checked
              }))}
            />
            <span>Activar ChatGPT para conversaciones con clientes</span>
          </label>
          <small className="form-help">
            Cuando está activado, ChatGPT responderá automáticamente a los mensajes de texto de los clientes usando los prompts configurados.
          </small>
        </div>

        <div className="form-group" style={{ marginTop: '30px' }}>
          <h4 style={{ marginBottom: '15px' }}>Prompts de ChatGPT</h4>
          <p className="config-help" style={{ marginBottom: '15px' }}>
            Los prompts definen cómo ChatGPT responderá a los clientes. Puedes agregar múltiples prompts y activar/desactivar cada uno.
          </p>

          {/* Lista de prompts existentes */}
          {chatgptConfig.chatgptPrompts.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              {chatgptConfig.chatgptPrompts.map((prompt) => (
                <div key={prompt.id} style={{
                  padding: '15px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  marginBottom: '10px',
                  backgroundColor: prompt.active ? '#f8f9fa' : '#fff'
                }}>
                  {editingPrompt === prompt.id ? (
                    <div>
                      <input
                        type="text"
                        value={prompt.name}
                        onChange={(e) => setChatgptConfig(prev => ({
                          ...prev,
                          chatgptPrompts: prev.chatgptPrompts.map(p =>
                            p.id === prompt.id ? { ...p, name: e.target.value } : p
                          )
                        }))}
                        placeholder="Nombre del prompt"
                        style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
                      />
                      <textarea
                        value={prompt.prompt}
                        onChange={(e) => setChatgptConfig(prev => ({
                          ...prev,
                          chatgptPrompts: prev.chatgptPrompts.map(p =>
                            p.id === prompt.id ? { ...p, prompt: e.target.value } : p
                          )
                        }))}
                        placeholder="Instrucciones para ChatGPT..."
                        rows="4"
                        style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
                      />
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          type="button"
                          onClick={() => handleUpdatePrompt(prompt.id, {
                            name: prompt.name,
                            prompt: prompt.prompt
                          })}
                          style={{ padding: '6px 12px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Guardar
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingPrompt(null)}
                          style={{ padding: '6px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h5 style={{ margin: 0 }}>{prompt.name}</h5>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={prompt.active}
                              onChange={() => togglePromptActive(prompt.id, prompt.active)}
                            />
                            <span style={{ fontSize: '12px' }}>Activo</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => setEditingPrompt(prompt.id)}
                            style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePrompt(prompt.id)}
                            style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                      <div style={{
                        padding: '10px',
                        backgroundColor: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#495057',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {prompt.prompt}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Formulario para agregar nuevo prompt */}
          <div style={{
            padding: '15px',
            border: '2px dashed #dee2e6',
            borderRadius: '6px',
            backgroundColor: '#f8f9fa'
          }}>
            <h5 style={{ marginTop: 0, marginBottom: '10px' }}>➕ Agregar Nuevo Prompt</h5>
            <input
              type="text"
              value={newPrompt.name}
              onChange={(e) => setNewPrompt(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Nombre del prompt (ej: Respuesta General, Soporte Técnico, etc.)"
              style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
            />
            <textarea
              value={newPrompt.prompt}
              onChange={(e) => setNewPrompt(prev => ({ ...prev, prompt: e.target.value }))}
              placeholder="Instrucciones para ChatGPT. Ejemplo: 'Eres un asistente amable y profesional. Responde a las preguntas de los clientes de manera clara y concisa...'"
              rows="5"
              style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
            />
            <button
              type="button"
              onClick={handleAddPrompt}
              style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              ➕ Agregar Prompt
            </button>
          </div>
        </div>
      </div>

      <div className="config-actions">
        <div className="action-buttons">
          <button
            type="button"
            onClick={onClose}
            className="cancel-button"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="save-button"
            disabled={saving}
          >
            {saving ? 'Guardando...' : '💾 Guardar Configuración'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ConfigModal;

